import {ITranslationActionHandler, ITranslationInitialState, IUpdateTranslations} from './interfaces';
import {CHANGE_LOCALE, TranslationActionsType, UPDATE_TRANSLATIONS} from './actions';
import {languages, SystemLanguage} from 'utils/constants/constants';

// Resolved synchronously, before the store exists and before any component mounts or
// fetches translations. Every page's own loadTranslation call reads `getState().translation.locale`
// at dispatch time, so if this is wrong at t=0, everything fetches the wrong locale's files first
// and only corrects itself after a second round-trip - the gap in between is a visible flash of
// missing-translation fallbacks. Doing this here instead of in a post-mount useEffect (which
// necessarily races against every other component's first-render fetch) avoids that gap entirely.
function getInitialLocale(): string {
  const storedLocale = localStorage.getItem('locale');

  if (storedLocale && languages.some((lang) => lang.value === storedLocale)) {
    return storedLocale;
  }

  const browserLanguage = navigator.language?.split('-')[0];

  if (browserLanguage === SystemLanguage.Spanish) {
    return SystemLanguage.Spanish;
  }

  return SystemLanguage.English;
}

const translationInitialState: ITranslationInitialState = {
  locale: getInitialLocale(),
  translationKeys: [],
  translations: {
    en: {},
    es: {},
    zh: {},
    fr: {},
    de: {},
    hi: {},
    ml: {},
    pt: {},
    fil: {},
    ta: {},
    vi: {},
    km: {},
    fa: {},
    it: {},
    el: {},
    ar: {},
    ja: {},
    prs: {},
    ps: {},
    ko: {}
  }
};

function changeLocale({
  state,
  payload
}: ITranslationActionHandler): ITranslationInitialState {
  return {
    ...state,
    locale: payload as string
  };
}

function updateTranslations({
  state,
  payload
}: ITranslationActionHandler): ITranslationInitialState {
  const {
    locale,
    translationKey,
    translations: newTranslations
  } = payload as IUpdateTranslations;
  const translationsList = {
    ...state.translations[locale],
    ...newTranslations
  };
  const translationKeys = state.translationKeys;

  if (translationKey && !translationKeys.includes(translationKey)) {
    translationKeys.push(translationKey);
  }

  return {
    ...state,
    translationKeys: [...translationKeys],
    translations: {
      ...state.translations,
      [locale]: {...translationsList}
    }
  };
}

const translationReducerHandlers = new Map([
  [CHANGE_LOCALE, changeLocale],
  [UPDATE_TRANSLATIONS, updateTranslations]
]);

const translationReducer = (state = translationInitialState, action: TranslationActionsType): ITranslationInitialState => {
  const handler = translationReducerHandlers.get(action.type);

  return (handler && {
    ...handler(<ITranslationActionHandler>{
      state,
      payload: action.payload
    })
  }) || {...state};
};

export default translationReducer;
